import * as React from "react";
import { DateTimeAccuracy, LocalizedDateTime } from "../../../misc/components/LocalizedDateTime";
import { CommentModals } from "../../Shared/CommentsModals";
import { FixedCommentsSelectors } from "../../Shared/FixedCommentsSelectors";
import { PFTResults, PressureResults, StudyRelevantParameters } from "../../Shared/PFTAPITypes";
import { PFTResultParameters } from "../../Shared/PFTResultsParameters";
import RepeatabilityTable from "../../Shared/RepeatabilityTable";
import { SelectedSessionCommentsCount, SelectedTestCommentsCount } from "../../Shared/Selectors/FixedCommentSelectors";
import { TestAcceptabilityTable } from "../../Shared/TestAcceptabilityTable";
import { AcceptabilityState, ToggleTestAcceptabilityAction } from "../../Shared/UseAcceptability";
import { FixedCommentsState, LoadAnalysisSetOverreadAction, PageValidatedAction, ToggleSessionFixedCommentsVisibilityAction, ToggleTestFixedCommentsVisibilityAction } from "../../Shared/UseFixedComments";
import { QualityAcceptabilityTable } from "../../Shared/QualityAcceptabilityTable";
import { GraphState, ToggleTestGraphVisibilityAction } from "../UsePressureGraph";
import { useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";
import { SnipSession } from "webportalclient";
import { NextGenFixedCommentsState } from "../../Spirometry/Testing/UseNextGenFixedComments";
import { OverreadingResultsComments } from "../../Shared/OverreadingResultsComments";
import { ResultsContext } from "../../Spirometry/UseResultsContext";

export interface SnipResultsTableProps {
    isViewOnly: boolean;
    isRejected: boolean;
    isSiteAnalysisSet: boolean;
    resultData: PressureResults;
    session: SnipSession;
    fixedCommentsStateOverreading: NextGenFixedCommentsState;
    fixedCommentsDispatchOverreading;
    fixedCommentsStateTechnician: NextGenFixedCommentsState;
    fixedCommentsDispatchTechnician;
    acceptabilityState: AcceptabilityState;
    acceptabilityDispatch;
    shouldValidateFixedComments: boolean;
    studySnipRelevantParameters?: StudyRelevantParameters;
    graphState: GraphState;
    graphDispatch;
}

export const SnipResultsTable = (props: SnipResultsTableProps) => {
    return (
        <>
            <ResultsContext.Provider value={{ showBestValues: true, showPredictedValues: false, showPercentagePredicted: false, showReversibilityBaseValue: false, showReversibilityAbsoluteChange: false, showReversibilityPercentageChange: false }}>
                <table id="SnipResultsTable" className="table table-striped">
                    <thead>
                        <tr>
                            <th></th>
                            {
                                props.resultData.TestResults.sort((a, b) => a.TestNumber - b.TestNumber).map(t =>
                                    <th key={`TestHeader${t.TestId}`} style={{ color: "#000" }}>{t.TestNumber}</th>
                                )
                            }
                            <th>Best</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Display</td>
                            {props.graphState.flow.Tests.sort((a, b) => a.TestNumber - b.TestNumber).map(t => (
                                <td key={`TestVisibilityToggle${t.TestId}`}>
                                    <input
                                        checked={t.IsDisplayed}
                                        type="checkbox"
                                        onChange={() =>
                                            props.graphDispatch(
                                                ToggleTestGraphVisibilityAction.create(t.TestId)
                                            )
                                        }
                                    />
                                </td>
                            ))}
                            <td />
                        </tr>
                        <tr>
                            <td>Time</td>
                            {
                                props.resultData.TestResults.sort((a, b) => a.TestNumber - b.TestNumber).map(t =>
                                    <td key={`TestDateTime${t.TestId}`}>
                                        <LocalizedDateTime time={t.TestDateTime} accuracy={DateTimeAccuracy.Second} />
                                    </td>
                                )
                            }
                            <td></td>
                        </tr>

                        <PFTResultParameters
                            isLoading={props.acceptabilityState.loadingBestValues}
                            results={props.resultData}
                            isRejected={props.isRejected}
                            studyRelevantParameters={props.studySnipRelevantParameters}
                            showConfiguredParametersOnly={true}
                        />

                        <RepeatabilityTable
                            isLoading={props.acceptabilityState.loadingBestValues}
                            results={props.resultData}
                            isRejected={props.isRejected}
                            studyRelevantParameters={props.studySnipRelevantParameters}
                            showConfiguredParametersOnly={true}
                        />

                        <QualityAcceptabilityTable results={props.resultData} />

                        <TestAcceptabilityTable
                            isViewOnly={props.isViewOnly}
                            isLoading={props.acceptabilityState.loadingBestValues}
                            testResults={props.acceptabilityState.spirometryResults.TestResults}
                            displayTechnicianAcceptability={!props.isSiteAnalysisSet}
                            onAcceptabilityChange={(id, checked) =>
                                props.acceptabilityDispatch(
                                    ToggleTestAcceptabilityAction.create(id, checked)
                                )
                            }
                        />

                        <OverreadingResultsComments
                            viewOnly={props.isViewOnly}
                            session={props.session}
                            showTechnician={true}
                            fixedCommentsDispatchTechnician={props.fixedCommentsDispatchTechnician}
                            fixedCommentsStateTechnician={props.fixedCommentsStateTechnician}
                            fixedCommentsDispatchOverreading={props.fixedCommentsDispatchOverreading}
                            fixedCommentsStateOverreading={props.fixedCommentsStateOverreading}
                        />

                    </tbody>
                </table>
            </ResultsContext.Provider>
        </>
    );
}
